import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import { ArrowUpDown, ArrowUp, ArrowDown, Building2, CheckCircle, Clock, AlertCircle, Plus } from 'lucide-react'

import AdminFacilityRow from './AdminFacilityRow/AdminFacilityRow'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import { Table, TableHeader, TableBody, TableHead, TableRow } from '../ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { requestCreator } from '../../utilities/requests'
import useAuth from '../../utilities/hooks/useAuth'

const { get: getFacilities, cancel: cancelFacilityRequests } = requestCreator()

function AdminFacilitiesIndex() {
  const [isLoading, setIsLoading] = useState(true)
  const [facilities, setFacilities] = useState([])
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const { replace: redirect, push: navigate } = useHistory()
  const { currentUser } = useAuth()

  useEffect(() => {
    getFacilities('/api/admin/facilities', {
      params: {
        fields: {
          facility:
            'name,slug,subscriptionStatus,trialEnd,isStripeConnected,stripeCreditCardBrand,stripeCreditCardLast4,timeZone,numberOfStudentsEnrolledThisMonth,lastTimeSkillMarked,lastDirectorSignInAt,lastKlassStartDatetime,nextKlassStartDatetime,lastContactedAt,totalStudentsCount,totalCoachesCount,totalDirectorsCount,activeKlassesCount'
        }
      }
    })
      .then(setFacilities)
      .catch((error) => {
        if (axios.isCancel(error)) {
          return
        } else if (error.response.status == 401) {
          redirect('/login')
        } else {
          throw error
        }
      })
      .finally(() => setIsLoading(false))
    return cancelFacilityRequests
  }, [redirect])

  function handleSort(key) {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  function getSortIcon(columnKey) {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="ml-1 h-4 w-4 inline" />
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4 inline" />
    )
  }

  function getSubscriptionBadge(status) {
    if (!status) return <Badge variant="outline">None</Badge>

    const variants = {
      demo: { variant: 'secondary', text: 'Demo' },
      active: { variant: 'default', className: 'bg-green-600', text: 'Active' },
      trialing: { variant: 'default', className: 'bg-blue-600', text: 'Trialing' },
      past_due: { variant: 'destructive', text: 'Past Due' },
      canceled: { variant: 'outline', text: 'Canceled' },
      incomplete: { variant: 'outline', text: 'Incomplete' }
    }

    const config = variants[status] || { variant: 'secondary', text: status }
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.text}
      </Badge>
    )
  }

  const sortedFacilities = React.useMemo(() => {
    if (!sortConfig.key) return facilities

    return [...facilities].sort((a, b) => {
      const aValue = a[sortConfig.key]
      const bValue = b[sortConfig.key]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      if (typeof aValue === 'string') {
        const comparison = aValue.localeCompare(bValue)
        return sortConfig.direction === 'asc' ? comparison : -comparison
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [facilities, sortConfig])

  const stats = React.useMemo(() => {
    const total = facilities.length
    const active = facilities.filter((f) => f.subscriptionStatus === 'active').length
    const trialing = facilities.filter((f) => f.subscriptionStatus === 'trialing').length
    const issues = facilities.filter(
      (f) => f.subscriptionStatus === 'past_due' || f.subscriptionStatus === 'canceled'
    ).length

    return { total, active, trialing, issues }
  }, [facilities])

  return (
    <ApplicationLayout isContentLoading={isLoading}>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 pt-6">
          <h1 className="text-2xl font-semibold text-gray-900">Facilities</h1>
          {currentUser.superuser && (
            <Button onClick={() => navigate('/facilities/new')} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="h-4 w-4 mr-1" />
              Add Facility
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Facilities</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.active}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Trialing</CardTitle>
              <Clock className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.trialing}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Needs Attention</CardTitle>
              <AlertCircle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.issues}</div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:hidden space-y-4">
          {sortedFacilities.map((facility) => (
            <Card key={facility.id}>
              <CardHeader>
                <CardTitle className="text-lg">
                  <a href={`/facilities/${facility.id}/edit`} className="text-purple-600 hover:text-purple-700">
                    {facility.name}
                  </a>
                </CardTitle>
                <p className="text-sm text-gray-500">ID: {facility.id}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Subscription</span>
                  {getSubscriptionBadge(facility.subscriptionStatus)}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Total Students</span>
                  <span className="font-semibold">{facility.totalStudentsCount || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">This Month</span>
                  <span className="font-semibold">{facility.numberOfStudentsEnrolledThisMonth || 0}</span>
                </div>
                <div className="flex items-center justify-between gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/facilities/${facility.id}/coaches_dashboard`)}
                  >
                    Coach Dashboard
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/facilities/${facility.id}/edit`)}>
                    Edit Facility
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('name')}>
                  Name {getSortIcon('name')}
                </TableHead>
                <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('id')}>
                  Facility ID {getSortIcon('id')}
                </TableHead>
                <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('subscriptionStatus')}>
                  Subscription {getSortIcon('subscriptionStatus')}
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-gray-50 text-center"
                  onClick={() => handleSort('totalStudentsCount')}
                >
                  Total Students {getSortIcon('totalStudentsCount')}
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-gray-50 text-center"
                  onClick={() => handleSort('numberOfStudentsEnrolledThisMonth')}
                >
                  This Month {getSortIcon('numberOfStudentsEnrolledThisMonth')}
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedFacilities.map((facility) => (
                <AdminFacilityRow key={facility.id} facility={facility} />
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </ApplicationLayout>
  )
}

AdminFacilitiesIndex.defaultProps = {}

AdminFacilitiesIndex.propTypes = {}

export default AdminFacilitiesIndex
